/**
 * Flow Studio Envelope Strategy
 * Handles unwrapping/wrapping of Flow Studio API responses
 * Response format: { success: boolean, data?: T, error?: { code, message, details } }
 */

import { EnvelopeStrategy } from '@atlas-forms/form-actions';

export class FlowStudioEnvelopeStrategy implements EnvelopeStrategy {
  key = 'flow-studio';

  /**
   * Extract data from Flow Studio response
   * Unwraps { success, data, error } envelope to get the actual data
   */
  extract(response: unknown): unknown {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const r = response as any;
    // BaseApiClient runs convertKeysToPascalCase before calling extract,
    // so envelope keys arrive as PascalCase (Success/Data). Support both.
    const success = r.Success ?? r.success;
    const data = r.Data ?? r.data;

    if (success === true && data !== undefined) return data;
    if (data !== undefined) return data;
    return response;
  }

  /**
   * Wrap fields in Flow Studio response format
   */
  wrap(fields: Record<string, unknown>): unknown {
    return {
      success: true,
      data: fields,
    };
  }

  /**
   * Extract errors from Flow Studio response
   */
  extractErrors(response: unknown): unknown[] {
    if (!response || typeof response !== 'object') {
      return [];
    }

    const r = response as any;
    const error = r.Error ?? r.error;
    const errors = r.Errors ?? r.errors;

    if (error) return [error];
    if (Array.isArray(errors)) return errors;
    return [];
  }
}
